<template>
  <PageHeader>
    <template #header-title>
      {{ $t('Clients') }}
    </template>
  </PageHeader>
  <GraphqlDataTable
    :query="query"
    :order-by="initialOrder"
    :column-black-list="['id']"
    :filter-black-list="['accounts']"
    :search-placeholder="$t('Search clients…')"
  >
    <template #action="{ row, index }">
      <StaffingAgencyClientActionColumn :row="row" :row-index="index" @open-page="openClientPage" />
    </template>
  </GraphqlDataTable>
  <Teleport :to="drawerTarget">
    <ClientDrawer
      :show="showDrawer"
      :client-relation-id="drawerClientId ?? ''"
      @update:show="onDrawerToggle"
      @open-page="openClientPage"
    />
  </Teleport>
  <PageOverlayRegion
    :open="pageClientId !== null"
    :label="$t('Client page')"
    data-test="agency-client-page-overlay"
    @close="closeClientPage"
  >
    <ClientPage
      v-if="pageClientId"
      :client-relation-id="pageClientId"
      overlay
      @close="closeClientPage"
    />
  </PageOverlayRegion>
</template>

<script lang="ts" setup>
import { TypedQueryDocumentNode } from 'graphql/index'
import { useOverlayPage } from '@/composables/useOverlayPage'
import PageOverlayRegion from '@/components/PageOverlayRegion.vue'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import PageHeader from '@/components/PageHeader.vue'
import ClientDrawer from '@/components/Clients/ClientDrawer.vue'
import { CLIENT_ID_KEY, useClientDrawer } from '@/components/Clients/useClientDrawer'
import ClientPage from '@/components/Clients/ClientPage.vue'
import StaffingAgencyClientActionColumn from '@/components/GraphqlDataTable/columns/StaffingAgencyClient/StaffingAgencyClientActionColumn.vue'
import { graphql } from '@/gql'
import { SortOrder, StaffingAgencyClientOrderByColumn } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import GlobalEncoder from '@/utils/Encoders/GlobalEncoder'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import type { PeopleSegment } from '@/components/Clients/clientPageTabs'
import { peopleQuery } from '@/components/Clients/People/peopleLinks'

const query = graphql(`
  query StaffingAgencyClientsTableQuery(
    $accounts: [ID!]
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [StaffingAgencyClientOrderByClauseInput!]
    $isDirect: Boolean
    $clientRegions: MarketRegionInput
    $createdAtDateRange: DateRangeInput
  ) {
    staffingAgencyClients(
      accounts: $accounts
      search: $search
      page: $page
      first: $first
      orderBy: $orderBy
      isDirect: $isDirect
      clientRegions: $clientRegions
      createdAtDateRange: $createdAtDateRange
    ) {
      data {
        id
        ...StaffingAgencyClientNameColumn
        ...StaffingAgencyClientRelationColumn
        ...StaffingAgencyClientPlacementsColumn
        ...StaffingAgencyClientLocationColumn
        ...StaffingAgencyClientAddedColumn
        ...StaffingAgencyClientActionColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const initialOrder = {
  field: StaffingAgencyClientOrderByColumn.ClientName,
  order: SortOrder.Asc,
}

const { drawerTarget, drawerClientId, showDrawer, onDrawerToggle } = useClientDrawer()

const {
  id: pageClientId,
  open: openPage,
  close: closeClientPage,
} = useOverlayPage({
  backLink: laravelNamedRoute(namedRoutes.frontend.clients.index, {}),
  viewUrl: (supplierClientId: string) =>
    laravelNamedRoute(namedRoutes.frontend.clients.view, { companySupplier: supplierClientId }),
  idFromPath: (path) => {
    const [, id] = path.match(/\/clients\/([^/?#]+)/) ?? []
    return id && id !== 'onboarding' ? decodeURIComponent(id) : null
  },
})

function openClientPage(supplierClientId: string, segment?: PeopleSegment) {
  onDrawerToggle(false)
  // Straight to the encoder: the debounced push would land after the pathname changed and leave ?client-id= behind.
  GlobalEncoder.push({ key: CLIENT_ID_KEY, query: null })
  GlobalEncoder.forceUpdateHistory()
  openPage(supplierClientId, segment ? peopleQuery(segment) : undefined)
}
</script>
